import { useEffect } from "react";
import { Check } from "lucide-react";
import StoreBadges from "@/components/site/StoreBadges";
import { SITE } from "@/constants/testIds";
import { trackEvent } from "@/lib/analytics";
import { useMeta } from "@/hooks/useMeta";

/* Tier config, everything the page renders comes from here. */
const TIERS = [
  {
    id: "free",
    testId: SITE.pricingTierFree,
    name: "Free",
    tagline: "Protected from today.",
    price: "Free",
    period: "",
    blurb: "No credit card. No trial clock. You get the seal from the first tap.",
    features: [
      "You start recording manually; the app captures audio, timestamp, and GPS until you end the session.",
      "Seal and encrypt your recordings at capture",
      "Unlimited “Keeps” (mark anything, keep it for the trial duration)",
      "Local storage + 20 MB cloud backup",
    ],
    featured: false,
  },
  {
    id: "plus",
    testId: SITE.pricingTierPlus,
    name: "Plus",
    tagline: "Room to keep what matters.",
    price: "$5.99",
    period: "/ month",
    blurb: "For people who record often and want everything backed up off the phone.",
    features: [
      "5 GB sealed cloud storage",
      "Unlimited Keeps",
      "Multi-device restore",
      "Everything in Free",
    ],
    featured: true,
  },
  {
    id: "pro",
    testId: SITE.pricingTierPro,
    name: "Pro",
    tagline: "Maximum protection.",
    price: "$9.99",
    period: "/ month",
    blurb: "For frequent, high-stakes use, journalists, at-risk professionals, high-conflict situations.",
    features: [
      "10 GB sealed cloud storage",
      "Everything in Plus",
    ],
    featured: false,
  },
];

const FAQ = [
  {
    q: "What happens when my storage is full?",
    a: "The oldest recording is overwritten to make room for the next one, a rolling overwrite. Anything you have marked as “Keep” is protected from that overwrite and stays sealed for as long as your plan is active.",
  },
  {
    q: "Can I cancel any time?",
    a: "Yes. Cancel from inside the app. You keep access for the rest of the period you already paid for; nothing else charges after that.",
  },
  {
    q: "Is the Free tier really free?",
    a: "Yes, for the trial period. No credit card, no bait. Free covers local storage and 20 MB of cloud backup, and it includes unlimited Keeps.",
  },
  {
    q: "How do I get a copy of my sealed recording?",
    a: "Recordings are released only by legal order or signed release along with your encryption/decryption key. On approval, YeboNadi decrypts the record and delivers it via secure cloud storage. A retrieval fee applies, refer to the app for current retrieval pricing.",
  },
];

function TierCard({ tier }) {
  return (
    <div
      data-testid={tier.testId}
      className={`relative rounded-2xl bg-white border p-6 sm:p-8 flex flex-col ${
        tier.featured
          ? "border-[color:var(--yn-primary)] shadow-sm ring-1 ring-[color:var(--yn-primary)]/10"
          : "border-[color:var(--yn-border)]"
      }`}
    >
      {tier.featured && (
        <span
          className="absolute -top-3 left-6 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold text-white"
          style={{ background: "var(--yn-primary)" }}
        >
          Most popular
        </span>
      )}

      <p className="font-heading text-2xl font-semibold text-[color:var(--yn-ink)] tracking-tight">
        {tier.name}
      </p>
      <p className="mt-1 text-sm font-medium text-[color:var(--yn-primary)]">
        {tier.tagline}
      </p>

      <div className="mt-5 flex items-baseline gap-1.5">
        <span className="font-heading text-[42px] sm:text-5xl font-bold text-[color:var(--yn-ink)] tracking-tight">
          {tier.price}
        </span>
        {tier.period && (
          <span className="text-sm text-[color:var(--yn-muted)]">{tier.period}</span>
        )}
      </div>

      <p className="mt-4 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
        {tier.blurb}
      </p>

      <ul className="mt-6 space-y-3 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[15px]">
            <Check
              size={18}
              className="mt-0.5 shrink-0"
              style={{ color: "var(--yn-primary)" }}
            />
            <span className="text-[color:var(--yn-ink)]">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 pt-6 border-t border-[color:var(--yn-border)]">
        <p className="text-xs font-medium uppercase tracking-wider text-[color:var(--yn-muted)] mb-3">
          Get the app · purchase from inside
        </p>
        <StoreBadges
          placement={`pricing-tier-${tier.id}`}
          appTestId={`pricing-tier-${tier.id}-app-store-badge`}
          playTestId={`pricing-tier-${tier.id}-play-store-badge`}
        />
      </div>
    </div>
  );
}

export default function Pricing() {
  useMeta({
    title: "Pricing, YeboNadi",
    description:
      "Three tiers. Free forever. Plus at $5.99/month for 5 GB of sealed cloud storage. Pro at $9.99/month for 10 GB and priority upload batching. Purchase happens inside the app.",
    path: "/pricing",
  });

  useEffect(() => {
    trackEvent("pricing_view", { placement: "pricing-page" });
  }, []);

  return (
    <div data-testid={SITE.pricingPage}>
      {/* HERO */}
      <section className="pt-14 sm:pt-20 lg:pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            Pricing
          </p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[color:var(--yn-ink)] max-w-3xl">
            Simple. Public. Nothing hidden.
          </h1>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed text-[color:var(--yn-muted)] max-w-2xl">
            Three tiers. Every tier starts with the same seal. Pay when you need
            more room to keep what you record. Purchase happens inside the app ,
            install first, choose your tier from your phone.
          </p>
        </div>
      </section>

      {/* TIER TABLE */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6"
            data-testid={SITE.pricingTable}
          >
            {TIERS.map((t) => (
              <TierCard key={t.id} tier={t} />
            ))}
          </div>

          <p className="mt-6 text-sm text-[color:var(--yn-muted)]">
            Prices in USD. Billed monthly.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-16 md:py-24 border-y border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)]"
        data-testid={SITE.pricingCta}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
              Pricing FAQ
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
              The billing questions people actually ask.
            </h2>
          </div>

          <dl className="max-w-3xl divide-y divide-[color:var(--yn-border)]">
            {FAQ.map((f) => (
              <div key={f.q} className="py-6">
                <dt className="font-heading text-lg font-semibold text-[color:var(--yn-ink)]">
                  {f.q}
                </dt>
                <dd className="mt-2 text-[16px] leading-relaxed text-[color:var(--yn-muted)]">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* UNDERTAKING LINE */}
      <section className="py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-heading text-xl sm:text-2xl font-semibold text-[color:var(--yn-ink)] leading-snug">
            Every plan, including Free, requires signing the YeboNadi Undertaking.
          </p>
        </div>
      </section>
    </div>
  );
}
